import React, { CSSProperties, FC, useEffect, useState } from 'react';
import { WarningAltOutlined } from '@duxton-web/icons';
import {
  ConfigProvider,
  Flex,
  Form,
  Input,
  Select,
  theme,
  Tooltip,
  Typography,
} from '@duxton-web/ui';
import { DefaultOptionType } from '@duxton-web/ui/es/select';
import { useQueries, useQuery } from '@tanstack/react-query';
import { produce } from 'immer';
import { debounce } from 'lodash';

import FacetComponent from './FacetComponent';
import facetMap, { ConstraintOperator } from './facetMap';
import {
  ConstraintValueItemType as ConstraintValueItemTypeEnum,
  CustomInputFacets,
  GroupType,
  ValueItemTypeSupportedOperator,
} from '@/common/const';
import { isProduction } from '@/common/environment';
import {
  detectValueItemType,
  getValueItemTypeOptions,
} from '@/common/valueItemType';
import { FACETS } from '@/constants/facets';
import Service, {
  availableFacetValuesMap,
  Constraint,
  ConstraintType,
  VariableInfo,
} from '@/lib/App';
import { ExpFormField } from '@/pages/ExperimentFormV2/helper/consts';
import { getVariableStatsStartDate } from '@/utils/rollout';

const { Text } = Typography;

interface ConstraintProps {
  disabled?: boolean;
  isCustomConstraint: boolean;
  index: number;
  allowedFacets?: any;
  constraint: Constraint;
  hasError?: boolean;
  onChange?: (index: number, constraint: Constraint) => void;
  style?: CSSProperties;
  onlyEqualOperator?: boolean;
  rolloutVariable?: VariableInfo;
  showFacetStatus?: boolean;
  disableTarget?: boolean;
  testingPurpose?: boolean;
  mandatoryFacets?: string[];
  constraints?: Constraint[];
  variableNames?: string[];
}

export const useVariableStatsQuery = (
  variables: string[],
  fromDate?: string,
) => {
  const q = variables
    ? variables.map((variable) => {
        return {
          queryKey: ['variableStats', variable],
          queryFn: () => Service.planner.fetchStats(variable, fromDate),
        };
      })
    : [];

  const queries = useQueries({ queries: q });

  const allConcurrentRequestsCompleted = queries.every(
    (query) => query.isSuccess,
  );
  return allConcurrentRequestsCompleted
    ? queries.map((query) => query.data)
    : [];
};

export const getProperlyInstrumentedFacetList = (data: any[]) => {
  if (!data.length) return [];

  let facetList = new Set();
  const variablesScopesFacetsArray = data.map((object) => object?.data?.scopes);
  for (const datum of variablesScopesFacetsArray) {
    for (const field in datum) {
      const { facets } = datum[field];
      const keys = Object.keys(facets);
      facetList = new Set([...facetList, ...keys]);
    }
  }

  return Array.from(facetList);
};

export function isImproperInstrumentedFacet(
  properlyInstrumentedFacetList: string[],
  facet: string,
): boolean {
  return (
    !properlyInstrumentedFacetList.includes(facet) && facet !== FACETS.Timestamp
  );
}

const tooltipTheme = {
  token: {
    colorBgSpotlight: theme.getDesignToken().colorBgBase,
  },
};

function ConstaintFacetSelector(props) {
  const {
    constraint,
    allowedFacets,
    disabled,
    disableTarget,
    showFacetStatus,
    properlyInstrumentedFacetList,
    handleChange,
    isFirstMandatoryOccurrence,
  } = props;

  function filterFacet(inputValue: string, option): boolean {
    const optionValue = option?.value;
    const optionLabel = allowedFacets.find(
      (facet) => facet.value === optionValue,
    )?.label;

    return optionLabel.toLowerCase().includes(inputValue.toLowerCase());
  }

  function getStatus(
    properlyInstrumentedFacetList: string[],
    facet: { label: string; value: string },
  ) {
    const hasImproperInstrumentedWarning =
      showFacetStatus &&
      isImproperInstrumentedFacet(properlyInstrumentedFacetList, facet.value);

    if (hasImproperInstrumentedWarning) {
      return (
        <ConfigProvider theme={tooltipTheme}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <Tooltip
              placement="top"
              title={
                <Typography.Text>
                  <Typography.Text strong type="danger">
                    Warning
                  </Typography.Text>
                  : Currently, no data is sent for this Variable-Facet
                  combination. Kindly work with your team to correctly set up
                  this facet and wait {isProduction ? 3 : 1.5} hours
                  post-deployment before proceeding.
                  <br />
                  <br />
                  GrabX ensures experiments are set up correctly by checking if
                  facets in the GetVariable() call are properly set up before
                  they can be selected here.{' '}
                  <Typography.Link
                    href="https://wiki.grab.com/display/EP/FAQ+%7C+Experiments"
                    target="blank"
                    style={{
                      color: 'lightblue',
                      textDecoration: 'underline',
                    }}
                  >
                    Docs
                  </Typography.Link>
                </Typography.Text>
              }
              trigger="hover"
            >
              <WarningAltOutlined style={{ color: '#faad14' }} />
            </Tooltip>
            <span>{facet.label}</span>
          </div>
        </ConfigProvider>
      );
    }
    return facet.label;
  }

  switch (constraint?.type) {
    case ConstraintType.CustomInt:
    case ConstraintType.CustomString:
      return (
        <Input
          readOnly
          value={`Custom Key (${constraint?.type === ConstraintType.CustomInt ? 'Integer' : 'String'})`}
          style={{
            width: 200,
          }}
        />
      );

    default: {
      let options = [];

      if (allowedFacets) {
        allowedFacets.forEach((facet) => {
          options.push({
            value: facet.value || '',
            label: getStatus(properlyInstrumentedFacetList, facet),
            disabled: facet.value === FACETS.Area,
          });
        });
      }

      return (
        <Select
          disabled={disabled || disableTarget || isFirstMandatoryOccurrence}
          style={{
            minWidth: 200,
          }}
          showSearch
          placeholder="Select Facet"
          onChange={(e) => handleChange({ value: e, name: 'target' })}
          value={constraint?.target}
          filterOption={filterFacet}
          options={options}
        />
      );
    }
  }
}

function CustomConstraintKey(props) {
  const { constraint, disabled, handleChange } = props;
  switch (constraint?.type) {
    case ConstraintType.CustomInt:
    case ConstraintType.CustomString:
      return (
        <Input
          disabled={disabled}
          showCount
          placeholder="Enter"
          defaultValue={constraint.target || undefined}
          style={{ maxWidth: 200 }}
          maxLength={5}
          onChange={debounce(
            (e) => handleChange({ value: e.target.value, name: 'target' }),
            200,
          )}
        />
      );
    default:
      return <></>;
  }
}

function ConstraintOperatorSelector(props) {
  const { constraint, onlyEqualOperator, disabled, isAreaFacet, handleChange } =
    props;
  let options = [];

  switch (constraint.type) {
    case ConstraintType.CustomString:
      options = [
        {
          label: ConstraintOperator.IN.label,
          value: ConstraintOperator.IN.label,
        },
        {
          label: ConstraintOperator.NOT_IN.label,
          value: ConstraintOperator.NOT_IN.label,
        },
        {
          label: ConstraintOperator.EQUALS.label,
          value: ConstraintOperator.EQUALS.value,
        },
        {
          label: ConstraintOperator.NOT_EQUALS.label,
          value: ConstraintOperator.NOT_EQUALS.value,
        },
        {
          label: ConstraintOperator.CONTAINS.label,
          value: ConstraintOperator.CONTAINS.value,
        },
        {
          label: ConstraintOperator.NOT_CONTAINS.label,
          value: ConstraintOperator.NOT_CONTAINS.value,
        },
      ];
      break;
    case ConstraintType.CustomInt:
      options = [
        {
          label: ConstraintOperator.IN.label,
          value: ConstraintOperator.IN.label,
        },
        {
          label: ConstraintOperator.NOT_IN.label,
          value: ConstraintOperator.NOT_IN.label,
        },
        {
          label: ConstraintOperator.EQUALS.label,
          value: ConstraintOperator.EQUALS.value,
        },
        {
          label: ConstraintOperator.NOT_EQUALS.label,
          value: ConstraintOperator.NOT_EQUALS.value,
        },
        {
          label: ConstraintOperator.LESS_THAN_EQUALS.label,
          value: ConstraintOperator.LESS_THAN_EQUALS.value,
        },
        {
          label: ConstraintOperator.GREATER_THAN_EQUALS.label,
          value: ConstraintOperator.GREATER_THAN_EQUALS.value,
        },
      ];
      break;
    default:
      // if facetMap is not configure properly for facet, return only equal operator
      if (!facetMap?.[constraint?.target]) {
        options = [ConstraintOperator.EQUALS];
      } else {
        options = facetMap[constraint?.target]?.op;
      }
  }

  return (
    <Select
      disabled={disabled || isAreaFacet}
      style={{ width: 'fit-content' }}
      placeholder="Select"
      onChange={(e) => handleChange({ value: e, name: 'op' })}
      options={onlyEqualOperator ? [ConstraintOperator.EQUALS] : options}
      value={constraint.op || undefined}
      popupMatchSelectWidth={false}
    />
  );
}

function ConstraintValueItemType(props) {
  const { constraint, disabled, isAreaFacet, handleChange } = props;

  if (
    constraint.target &&
    !CustomInputFacets.includes(constraint.target) &&
    constraint?.type === ConstraintType.Standard &&
    ValueItemTypeSupportedOperator.includes(constraint.op)
  ) {
    const isInlineOptionForMerchantFacet = (option: DefaultOptionType) =>
      option.label === ConstraintValueItemTypeEnum.INLINE.label &&
      constraint.target === FACETS.Merchant;

    return (
      <Select
        disabled={disabled || isAreaFacet}
        style={{ minWidth: 150 }}
        placeholder="Select"
        onChange={(e) => {
          handleChange({ value: e, name: 'valueItemType' });
        }}
        options={getValueItemTypeOptions(constraint).map((option) => ({
          ...option,
          label: isInlineOptionForMerchantFacet(option) ? (
            <>
              <ConfigProvider theme={tooltipTheme}>
                <Tooltip
                  title={
                    <Typography.Text>
                      <Typography.Text strong type="danger">
                        Warning
                      </Typography.Text>
                      : Inline is currently not supported for Merchant facet.
                    </Typography.Text>
                  }
                  placement="leftBottom"
                  styles={{
                    root: {
                      maxWidth: 190,
                    },
                  }}
                >
                  <WarningAltOutlined
                    style={{ color: '#faad14', marginRight: 4 }}
                  />
                </Tooltip>
              </ConfigProvider>
              {option.label}
            </>
          ) : (
            option.label
          ),
          disabled: isInlineOptionForMerchantFacet(option),
        }))}
        value={constraint.valueItemType || detectValueItemType(constraint)}
      />
    );
  }

  return <></>;
}

function ConstraintFacetComponent(props) {
  const {
    constraint,
    disabled,
    isAreaFacet,
    availableValues,
    testingPurpose,
    handleChange,
  } = props;

  if (!constraint?.op || !constraint?.target) return <></>;

  const isInlineValueItemTypeForMerchantFacet = (constraint: Constraint) =>
    constraint.valueItemType === ConstraintValueItemTypeEnum.INLINE.value &&
    constraint.target === FACETS.Merchant;

  return (
    <div
      style={{
        flexGrow: 1,
      }}
    >
      <FacetComponent
        mode="multiple"
        disabled={
          disabled ||
          isAreaFacet ||
          isInlineValueItemTypeForMerchantFacet(constraint)
        }
        constraint={constraint}
        handleChange={handleChange}
        availableValues={availableValues}
        facetName={constraint.target}
        testingPurpose={testingPurpose}
      />
    </div>
  );
}

const ConstraintEditor: FC<ConstraintProps> = ({
  disabled,
  index,
  allowedFacets,
  onlyEqualOperator,
  constraint,
  hasError,
  onChange,
  style,
  rolloutVariable,
  showFacetStatus,
  disableTarget,
  testingPurpose,
  mandatoryFacets = [],
  constraints = [],
  variableNames,
}) => {
  const [availableValues, setAvailableValues] = useState<{
    [key: string]: string;
  }>({});

  const isAreaFacet = constraint?.target === FACETS.Area;
  const isMandatory = mandatoryFacets.includes(constraint?.target);
  const firstOccurrenceIndex = constraints.findIndex(
    (c) => c.target === constraint?.target,
  );
  const isFirstMandatoryOccurrence =
    isMandatory && index === firstOccurrenceIndex;

  const form = Form.useFormInstance();
  const variables = getVariableNames();
  const fromDate = rolloutVariable ? getVariableStatsStartDate() : '';
  const variableStats = useVariableStatsQuery(variables, fromDate);
  const properlyInstrumentedFacetList = getProperlyInstrumentedFacetList(
    variableStats,
  ) as string[];

  function getVariableNames(): string[] {
    if (variableNames && variableNames.length > 0) {
      return variableNames;
    }

    const formVariables = form?.getFieldValue(ExpFormField.VARIABLES.ROOT);
    if (formVariables) {
      return Object.keys(formVariables);
    }
    if (rolloutVariable) {
      return [rolloutVariable.name];
    }
    return [];
  }

  const { isLoading, data, error } = useQuery({
    queryKey: ['availableValues', constraint?.target, constraint?.op],
    queryFn: () =>
      Service.planner.fetchGroupsV2(
        null,
        null,
        null,
        null,
        constraint?.target !== 'loc'
          ? constraint?.target
          : [
                ConstraintOperator.IN_AREA.value,
                ConstraintOperator.NOT_IN_AREA.value,
              ].includes(constraint?.op)
            ? 'area'
            : constraint?.target,
        GroupType.STATIC,
        false,
      ),
    retry: 3,
  });

  useEffect(() => {
    const availableValuesMap = data?.items?.reduce((acc, cv) => {
      acc[cv.name] = cv.name;
      return acc;
    }, {});
    setAvailableValues({
      ...availableValuesMap,
      ...availableFacetValuesMap?.[constraint?.target],
    });
  }, [constraint?.target, isLoading, data]);

  const handleChange = (e): void => {
    const { value, name } = e;
    switch (name) {
      case 'target':
        {
          const constraintCopy = produce(constraint, (draft) => {
            draft.target = value;
            draft.op = '';
            draft.value = '';
            delete draft['valueItemType'];
          });

          onChange(index, constraintCopy);
        }

        break;
      case 'valueItemType':
        onChange(index, { ...constraint, valueItemType: value, value: '' });
        break;
      case 'value':
        {
          const constraintCopy = JSON.parse(JSON.stringify(constraint));

          constraintCopy.value = value;

          onChange(index, constraintCopy);
        }

        break;
      case 'op':
        {
          const constraintCopy = produce(constraint, (draft) => {
            draft.op = value;
            draft.value = '';
            delete draft['valueItemType'];
          });

          onChange(index, constraintCopy);
        }

        break;
    }
  };

  return (
    <Flex
      data-testid="constraints-editor-wrapper"
      className={hasError ? 'antd-error-state' : ''}
      gap={8}
      wrap="wrap"
      style={style}
    >
      <ConstaintFacetSelector
        constraint={constraint}
        allowedFacets={allowedFacets}
        disabled={disabled}
        disableTarget={disableTarget}
        showFacetStatus={isProduction ? showFacetStatus : false}
        properlyInstrumentedFacetList={properlyInstrumentedFacetList}
        handleChange={handleChange}
        isFirstMandatoryOccurrence={isFirstMandatoryOccurrence}
      />
      <CustomConstraintKey
        constraint={constraint}
        disabled={disabled}
        handleChange={handleChange}
      />
      <ConstraintOperatorSelector
        constraint={constraint}
        onlyEqualOperator={onlyEqualOperator}
        disabled={disabled}
        isAreaFacet={isAreaFacet}
        handleChange={handleChange}
      />
      <ConstraintValueItemType
        constraint={constraint}
        disabled={disabled}
        isAreaFacet={isAreaFacet}
        handleChange={handleChange}
      />
      <ConstraintFacetComponent
        constraint={constraint}
        disabled={disabled}
        isAreaFacet={isAreaFacet}
        availableValues={availableValues}
        testingPurpose={testingPurpose}
        handleChange={handleChange}
      />
      {error && <Text type="danger">{String(error)}</Text>}
    </Flex>
  );
};

export default ConstraintEditor;
